import {em, px, rem, siteWidthPercentage, spx, unset, vh, vw} from './common'
import type {ConverterArguments, ConvertWidthToPx} from '../types'
import type {GridContainerLayout, SoftMargins} from '@wix/document-services-types'
import {getManifestInitialSize, getMax, getMin, getParentWidth, getStaleMeasurements, getValue} from '../utils'
import type {DataModelExtensionAPI} from '../../../dataModel/dataModel'
import type {ManifestExtensionAPI} from '../../../builderComponents/manifest'
import {DEFAULT_WIDTH} from '../constants'

export const convertWidthToPx: ConvertWidthToPx = {
    px,
    spx,
    em,
    rem,
    auto, // depends on parent width
    vw,
    vh,
    siteWidthPercentage,
    percentage, // depends on parent width
    unset,
    fr, // depends on width
    fit: getDefaultWidth,
    maxContent: getDefaultWidth,
    minContent: getDefaultWidth,
    MinMaxSize: minMaxSize,
    StretchWithSoftMargins: stretchWithSoftMargins,
    Calc: getDefaultWidth,
    CalcFormula: getDefaultWidth,
    SystemVariable: getDefaultWidth
}

function percentage(converterArguments: ConverterArguments): number {
    const {value} = converterArguments
    return (value / 100) * getParentWidth(converterArguments)
}

function auto(converterArguments: ConverterArguments): number {
    return getParentWidth(converterArguments)
}

function fr(converterArguments: ConverterArguments): number {
    const {measurementsMapAPI, calculate, compRef, layoutData, value} = converterArguments
    const {get} = measurementsMapAPI
    calculate.width(converterArguments)
    const width = get.width(compRef)

    const {containerLayout} = layoutData
    const {columns} = containerLayout as GridContainerLayout

    const nonFrColumnsWidth = columns
        .filter(column => column.type !== 'fr')
        .filter(column => getMin(column)?.type !== 'fr' && getMax(column)?.type !== 'fr')
        .reduce((nonFrWidth, column) => {
            nonFrWidth += convertWidthToPx[column.type]({...converterArguments, ...column})
            return nonFrWidth
        }, 0)

    let frTotal = columns.reduce((amount, column) => {
        amount += column.type === 'fr' ? column.value : 0

        const min = getMin(column)
        const max = getMax(column)
        if (min?.type === 'fr') {
            amount += min.value
        } else {
            amount += max?.type === 'fr' ? max.value : 0
        }

        return amount
    }, 0)
    if (frTotal === 0) {
        frTotal++
    }

    return ((width - nonFrColumnsWidth) / frTotal) * value
}

function stretchWithSoftMargins(converterArguments: ConverterArguments): number {
    const {value} = converterArguments
    if (!value?.margins) {
        return getDefaultWidth(converterArguments)
    }

    const parentWidth = getParentWidth(converterArguments)

    const {left, right} = (value as SoftMargins['value']).margins
    const leftMarginInPx = convertWidthToPx[left.type]({
        ...converterArguments,
        value: left.value
    })
    const rightMarginInPx = convertWidthToPx[right.type]({
        ...converterArguments,
        value: right.value
    })

    return parentWidth - leftMarginInPx - rightMarginInPx
}

function minMaxSize(converterArguments: ConverterArguments): number {
    const {min, max} = converterArguments
    const minPx =
        min &&
        convertWidthToPx[min.type]({
            ...converterArguments,
            value: getValue(min)
        })
    if (minPx) return minPx

    const maxPx =
        max &&
        convertWidthToPx[max.type]({
            ...converterArguments,
            value: getValue(max)
        })
    return maxPx ?? DEFAULT_WIDTH
}

export function getDefaultWidth({extensionAPI, compRef}: ConverterArguments) {
    const {dataModel, manifest} = extensionAPI as DataModelExtensionAPI & ManifestExtensionAPI

    const staleMeasurements = getStaleMeasurements(dataModel, compRef)
    const manifestInitialSize = getManifestInitialSize(manifest, compRef)

    return staleMeasurements?.width ?? manifestInitialSize?.width?.pixels ?? DEFAULT_WIDTH
}
